import React, { useState } from 'react'

import { TableColumn } from '../types'

// Rating stars component with hover support
function RatingStars({ 
  currentValue, 
  max, 
  onChange, 
  label 
}: { 
  currentValue: number
  max: number
  onChange: (value: number) => void
  label: string
}) {
  const [hoveredValue, setHoveredValue] = useState<number | null>(null)
  const displayValue = hoveredValue !== null ? hoveredValue : currentValue

  return (
    <div 
      className="flex gap-1" 
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={() => setHoveredValue(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation()
          e.preventDefault()
        }
      }}
      tabIndex={-1}
      role="group"
      aria-label={`Rating for ${label}`}
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
        <button
          key={star}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onChange(star)
            setHoveredValue(null)
          }}
          onMouseEnter={() => setHoveredValue(star)}
          className={`text-lg transition-colors ${
            star <= displayValue ? 'text-yellow-400' : 'text-gray-300'
          }`}
        >
          ★
        </button>
      ))}
    </div>
  )
}

interface CellContentProps<T> {
  column: TableColumn<T>
  row: T
  value: unknown
  rowIndex: number
  isEditing: boolean
  isDropdownOpen: boolean
  editingValue: string
  editingInputRef: React.RefObject<HTMLInputElement>
  setEditingValue: React.Dispatch<React.SetStateAction<string>>
  setHoveredCell: React.Dispatch<React.SetStateAction<{ rowIndex: number; columnKey: string } | null>>
  setTooltipPosition: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  handleEditStartWrapper: (rowIndex: number, columnKey: string, currentValue: string) => void
  handleEditSaveWrapper: (rowIndex: number, columnKey: string) => void
  handleEditCancelWrapper: () => void
  handleDropdownToggleWrapper: (rowIndex: number, columnKey: string) => void
  handleDatePickerToggleWrapper: (rowIndex: number, columnKey: string) => void
}

export function renderCellContent<T>({
  column,
  row,
  value,
  rowIndex,
  isEditing,
  isDropdownOpen,
  editingValue,
  editingInputRef,
  setEditingValue,
  setHoveredCell,
  setTooltipPosition,
  _handleEditStartWrapper,
  handleEditSaveWrapper,
  handleEditCancelWrapper,
  handleDropdownToggleWrapper,
  handleDatePickerToggleWrapper,
}: CellContentProps<T>) {
  // Inline editing
  if (isEditing && column.editable?.enabled) {
    return (
      <input
        ref={editingInputRef}
        type="text"
        value={editingValue}
        onChange={(e) => setEditingValue(e.target.value)}
        onBlur={() => handleEditSaveWrapper(rowIndex, column.key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleEditSaveWrapper(rowIndex, column.key)
          } else if (e.key === 'Escape') {
            handleEditCancelWrapper()
          }
        }}
        className="absolute inset-0 w-full h-full px-4 py-3 text-sm text-gray-700 bg-white border-none focus:outline-none"
        style={{
          boxSizing: 'border-box',
          boxShadow: 'inset 0 0 0 1px rgba(59, 130, 246, 0.4)',
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onClick={(e) => e.stopPropagation()}
        placeholder={column.editable.placeholder}
      />
    )
  }

  // Dropdown
  if (column.dropdown?.enabled) {
    const currentValue = column.dropdown.getCurrentValue ? column.dropdown.getCurrentValue(row) : value
    const currentOption = column.dropdown.options.find(opt => opt.value === currentValue)
    return (
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          handleDropdownToggleWrapper(rowIndex, column.key)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
            e.preventDefault()
            handleDropdownToggleWrapper(rowIndex, column.key)
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Open dropdown for ${column.label}`}
      >
        <span className="text-sm text-gray-900" style={{ color: isDropdownOpen ? '#1e40af' : undefined }}>
          {currentOption?.label || String(value || '')}
        </span>
        <svg
          className="w-4 h-4 transition-transform"
          style={{
            transform: isDropdownOpen ? 'rotate(180deg)' : undefined,
            color: isDropdownOpen ? '#2563eb' : '#9ca3af',
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    )
  }

  // DatePicker
  if (column.datePicker?.enabled) {
    const currentValue = column.datePicker.getCurrentValue ? column.datePicker.getCurrentValue(row) : value
    return (
      <span
        className="text-sm text-gray-900 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          handleDatePickerToggleWrapper(rowIndex, column.key)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
            e.preventDefault()
            handleDatePickerToggleWrapper(rowIndex, column.key)
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Open date picker for ${column.label}`}
      >
        {currentValue || 'Add date'}
      </span>
    )
  }

  // Rating (stars)
  if (column.rating?.enabled) {
    const currentValue = column.rating.getCurrentValue ? column.rating.getCurrentValue(row) : (value || 0)
    const max = column.rating.max || 5
    const [hoveredValue, setHoveredValue] = React.useState<number | null>(null)
    const displayValue = hoveredValue !== null ? hoveredValue : currentValue
    
    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <div 
        className="flex gap-1" 
        onClick={(e) => e.stopPropagation()}
        onMouseLeave={() => setHoveredValue(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
            e.preventDefault()
          }
        }}
        tabIndex={-1}
        role="group"
        aria-label={`Rating for ${column.label}`}
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
          <button
            key={star}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              column.rating!.onChange(row, star)
              setHoveredValue(null)
            }}
            onMouseEnter={() => setHoveredValue(star)}
            className={`text-lg transition-colors ${
              star <= displayValue ? 'text-yellow-400' : 'text-gray-300'
            }`}
          >
            ★
          </button>
        ))}
      </div>
    )
  }

  // Default render or custom render
  if (column.render) {
    return column.render(value, row, rowIndex)
  }

  // Tooltip support for truncated text
  if (column.showTooltip) {
    return (
      <span
        className="block truncate text-sm text-gray-900"
        onMouseEnter={(e) => {
          const span = e.currentTarget
          if (span.scrollWidth > span.clientWidth) {
            setHoveredCell({ rowIndex, columnKey: column.key })
            const rect = span.getBoundingClientRect()
            setTooltipPosition({
              top: rect.top - 8,
              left: rect.left,
            })
          }
        }}
        onMouseLeave={() => {
          setHoveredCell(null)
        }}
      >
        {String(value || '')}
      </span>
    )
  }

  return <span className="text-sm text-gray-900">{String(value || '')}</span>
}
